import React, { useState, useReducer } from "react";
import { Box, Button, Divider, Typography } from "@mui/material";
import GenericAlert from "../../components/GenericAlert";
import Loader from "../../components/Loader";
import { GenericAutocomplete } from "../../components/GenericAutocomplete";
import MerchantsExportTable from "../../components/tables/MerchantsExportTable";
import ConfirmationModal from "../../components/modals/ConfirmationModal";
import { useInternalMerchantsExport } from "../../customHooks/useInternalMerchantsExport";
import {
  INFO,
  MEDIUM,
} from "../../utils/constants";
import { useInternalClients } from "./shared/hooks/useInternalClients";
import { useClientDisabling } from "./shared/hooks/useClientDisabling";
import { IInternalClient } from "./shared/services/internalClientsService";
import SearchHeader from "./shared/components/SearchHeader";
import ListInternalClients from "../../components/tables/ListInternalClients";
import SaveIntegrationChanges from "../../components/tables/SaveIntegrationChanges";
import AddClientModal from "../../components/modals/AddClientModal";
import { integrationsModalReducer } from "../../reducers/integrationsReducers";
import IntegrationsModal from "./IntegrationsModals";

const EMPTY_MESSAGE = "No hay clientes integrados.";
const NO_RESULTS_MESSAGE = "No hay clientes para mostrar.";
const SEARCH_PLACEHOLDER = "Buscar por correo electrónico o alias";

const ALL_MARKETPLACE_OPTION = { id: "all", name: "Todos" };
const CONFIRM_EXPORT_TEXT =
  "Ya hay una exportación pendiente. Al confirmar, la solicitud anterior será eliminada y reemplazada por la nueva.";

const InternalMerchantsExportSection = () => {
  const {
    exports,
    loading,
    creating,
    blocked,
    showConfirmModal,
    alert,
    marketplaces,
    selectedMarketplaceIds,
    handleMarketplaceChange,
    handleCreate,
    handleConfirmCreate,
    handleCancelConfirm,
    handleDelete,
    clearAlert,
  } = useInternalMerchantsExport();

  const marketplaceOptions = marketplaces.map(({ id, name }) => ({
    id: id as string | number,
    name,
  }));

  const initialValues = {
    marketplaces:
      selectedMarketplaceIds.length === marketplaces.length
        ? [ALL_MARKETPLACE_OPTION.id]
        : selectedMarketplaceIds,
  };

  return (
    <Box mt={4}>
      <Divider className="m-b-20" />
      <Typography className="f-s-16 text-dark m-b-10">
        Exportaciones de sellers
      </Typography>
      <Box display="flex" alignItems="flex-end" gap={2} mb={1}>
        <Box flex={1}>
          <GenericAutocomplete
            label="Marketplaces a exportar"
            options={marketplaceOptions}
            allOption={ALL_MARKETPLACE_OPTION}
            initialValues={initialValues}
            handleChangeValue={handleMarketplaceChange}
            fieldKey="marketplaces"
            noOptionsText="No hay marketplaces disponibles"
          />
        </Box>
        <Button
          variant="contained"
          size="small"
          disabled={creating || blocked || !selectedMarketplaceIds.length}
          onClick={handleCreate}
          startIcon={<span className="material-symbols-rounded">download</span>}
        >
          {creating ? "Solicitando..." : "Nueva exportación"}
        </Button>
      </Box>
      {alert.showAlert && (
        <GenericAlert alert={alert} handleActionAlert={clearAlert} />
      )}
      <MerchantsExportTable
        exports={exports}
        loading={loading}
        onDelete={handleDelete}
        showMarketplace
        totalMarketplacesCount={marketplaces.length}
      />
      <ConfirmationModal
        open={showConfirmModal}
        onClose={handleCancelConfirm}
        onSubmit={handleConfirmCreate}
        text={CONFIRM_EXPORT_TEXT}
      />
    </Box>
  );
};

/**
 * Internal user view for accepted/integrated clients
 * Displays clients that have been accepted and can be enabled/disabled
 */
const InternalIntegrations: React.FC = () => {
  const [openModal, setOpenModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState<IInternalClient | null>(
    null,
  );
  const [modalState, modalDispatch] = useReducer(integrationsModalReducer, {
    showInfoMerchantModal: false,
    showDeleteModal: false,
    showDisableModal: false,
    showCancelModal: false,
  });

  const {
    data: clients,
    loading,
    pagination,
    alert,
    searchQuery,
    handlePageChange,
    handleSearch,
    handleClearSearch,
    refresh,
    setAlert,
  } = useInternalClients({ status: "accepted" });

  const {
    resetValue,
    handleDisableAction,
    handleSubmitDisabling,
    resetChanges,
    getDisabledClientNames,
    hasChanges,
  } = useClientDisabling({ onSuccess: refresh, setAlert });

  const handleSelectedAction = (option: string) => {
    modalDispatch({
      type: option === "delete" ? "showDeleteModal" : "showInfoMerchantModal",
      open: true,
    });
  };

  return (
    <>
      <SearchHeader
        placeholder={SEARCH_PLACEHOLDER}
        onSearch={handleSearch}
        onClear={handleClearSearch}
        buttonLabel="Añadir Cliente"
        onButtonClick={() => setOpenModal(true)}
        showButton={true}
      />

      {loading ? (
        <Loader size={MEDIUM} />
      ) : (
        <>
          {alert.showAlert && <GenericAlert alert={alert} />}

          {!alert.showAlert && clients.length === 0 && (
            <GenericAlert
              alert={{
                typeOfAlert: INFO,
                message: searchQuery ? NO_RESULTS_MESSAGE : EMPTY_MESSAGE,
                showAlert: true,
              }}
            />
          )}

          {clients.length > 0 && (
            <ListInternalClients
              clients={clients}
              handlePageChange={handlePageChange}
              pagination={pagination}
              showStatusColumn={false}
              handleDisableAction={handleDisableAction}
              handleSelectedAction={handleSelectedAction}
              setSelectedClient={setSelectedClient}
              resetValue={resetValue}
            />
          )}

          {hasChanges && (
            <SaveIntegrationChanges
              onSave={handleSubmitDisabling}
              onCancel={() =>
                modalDispatch({ type: "showCancelModal", open: true })
              }
            />
          )}
        </>
      )}

      <InternalMerchantsExportSection />

      <AddClientModal
        open={openModal}
        setOpen={setOpenModal}
      />

      <IntegrationsModal
        state={modalState}
        dispatch={modalDispatch}
        integration={selectedClient}
        entity="internal"
        disableMerchantProps={{
          merchants: getDisabledClientNames(),
          submitDisable: handleSubmitDisabling,
        }}
        onCancelSubmit={resetChanges}
        deleteIntegrationProps={{
          getIntegrationsOfMerchant: refresh,
        }}
      />
    </>
  );
};

export default InternalIntegrations;
